import { getIn } from 'formik'
import i18next from 'i18next'
import { LOCATIONS_RESET_CONFIG } from '../../../api/locations/formValuesConfig'
import { store } from '../../../store'
import { formatContactAsyncOption } from '../../../api/contacts/formatters'
import contacts from '../../../api/contacts/hooks'
import { setFormFilter } from '../../../store/formFilters'
import {
    baseAsyncSelectInput,
    baseCheckboxInput,
    baseDateInput,
    baseMultiLanguageEditorInput,
    baseMultiLanguageTextInput,
    baseMultiSelectInput,
    baseSelectInput,
    baseTextInput,
} from '../fieldTypes'
import { projectDescriptionFields, projectDetailSearchFields, projectGeneralFields } from './fields'

export const projectGeneralFieldTypes = () => {
    const { t } = i18next

    return {
        [projectGeneralFields.ID]: {
            ...baseTextInput,
            name: projectGeneralFields.ID,
            label: t('app.common.projectId'),
            disabled: true,
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_EXTERNAL_URL]: {
            ...baseTextInput,
            name: projectGeneralFields.PROJECT_EXTERNAL_URL,
            label: t('app.common.project.projectExternalUrl'),
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_STATUS_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.PROJECT_STATUS_ID,
            label: t('app.common.status'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_TYPE_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.PROJECT_TYPE_ID,
            label: t('app.common.project.type'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_SUBTYPE_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.PROJECT_SUBTYPE_ID,
            label: t('app.common.project.subtype'),
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_RECEIPT_DATE]: {
            ...baseDateInput,
            colProps: {
                sm: 12,
            },
            name: projectGeneralFields.PROJECT_RECEIPT_DATE,
            label: t('app.common.receiptDate'),
        },
        [projectGeneralFields.CONTRACT_NO]: {
            ...baseTextInput,
            name: projectGeneralFields.CONTRACT_NO,
            label: t('app.common.contractNumber'),
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.PROJECT_END_DATE]: {
            ...baseDateInput,
            name: projectGeneralFields.PROJECT_END_DATE,
            label: t('app.common.project.endDate'),
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.CONTRACT_DATE]: {
            ...baseDateInput,
            name: projectGeneralFields.CONTRACT_DATE,
            label: t('app.common.contractDate'),
            colProps: {
                sm: 12,
            },
        },
        [projectGeneralFields.OWNER_ID]: {
            ...baseAsyncSelectInput,
            name: projectGeneralFields.OWNER_ID,
            label: t('form.common.owner.nameOrCompany'),
            noLimit: true,
            searchCriteria: ['name'],
            colProps: {
                sm: 12,
            },
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
        },
        [projectGeneralFields.COUNTRY_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.COUNTRY_ID,
            label: t('app.common.country'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'country', value }))

                LOCATIONS_RESET_CONFIG.country.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${projectGeneralFields[fieldKey]}`, '')
                })
            },
        },
        [projectGeneralFields.COUNTY_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.COUNTY_ID,
            label: t('app.common.county'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'county', value }))

                LOCATIONS_RESET_CONFIG.county.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${projectGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.general, projectGeneralFields.COUNTRY_ID)
                return !countryValue || countryValue.length === 0
            },
        },
        [projectGeneralFields.CITY_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.CITY_ID,
            label: t('app.common.cityMunicipality'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'city', value }))

                LOCATIONS_RESET_CONFIG.city.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${projectGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const countyValue = getIn(formik?.values?.general, projectGeneralFields.COUNTY_ID)
                return !countyValue || countyValue.length === 0
            },
        },
        [projectGeneralFields.WIDER_AREA_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.WIDER_AREA_ID,
            label: t('app.common.widerArea'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'widerArea', value }))

                LOCATIONS_RESET_CONFIG.wider_area.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${projectGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const cityValue = getIn(formik?.values?.general, projectGeneralFields.CITY_ID)
                return !cityValue || cityValue.length === 0
            },
        },
        [projectGeneralFields.QUARTER_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.QUARTER_ID,
            label: t('app.common.quarter'),
            handleInputDisable: (formik) => {
                const widerAreaValue = getIn(formik?.values?.general, projectGeneralFields.WIDER_AREA_ID)
                return !widerAreaValue || widerAreaValue.length === 0
            },
        },
        [projectGeneralFields.ISLAND_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.ISLAND_ID,
            label: t('app.common.island'),
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.general, projectGeneralFields.COUNTRY_ID)
                return !countryValue || countryValue.length === 0
            },
        },
        [projectGeneralFields.STREET]: {
            ...baseTextInput,
            name: projectGeneralFields.STREET,
            label: t('app.common.street'),
        },
        [projectGeneralFields.STREET_NUMBER]: {
            ...baseTextInput,
            name: projectGeneralFields.STREET_NUMBER,
            label: t('app.common.streetNumber'),
        },
        [projectGeneralFields.STREET_SYNC]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 2,
                className: 'd-flex align-items-center',
            },
            name: projectGeneralFields.STREET_SYNC,
            label: t('form.common.streetSync.label'),
            tooltip: t('form.common.streetSync.tooltip'),
        },
        [projectGeneralFields.PROJECT_ENERGY_EFFICIENCY_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.PROJECT_ENERGY_EFFICIENCY_ID,
            label: t('app.common.energyEfficiency'),
        },
        [projectGeneralFields.PROJECT_WEB_SYNC]: {
            ...baseCheckboxInput,
            colProps: {
                className: 'd-flex align-items-center',
                sm: 12,
                xxl: 3,
            },
            name: projectGeneralFields.PROJECT_WEB_SYNC,
            label: t('app.common.project.webSync'),
        },
        [projectGeneralFields.AGENT_ID]: {
            ...baseSelectInput,
            name: projectGeneralFields.AGENT_ID,
            label: t('app.common.agent'),
            required: true,
        },
    }
}

export const projectDescriptionFieldTypes = (appSettings) => {
    const { t } = i18next

    return {
        [projectDescriptionFields.PROJECT_TITLE]: {
            ...baseMultiLanguageTextInput(appSettings.languages),
            name: projectDescriptionFields.PROJECT_TITLE,
            label: t('app.common.project.title'),
            description: t('form.project.description.message'),
            languageStyle: 'tabs',
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '108px' },
            },
            required: true,
            maxLenFeedback: 70,
            hint: t('form.project.description.hint'),
        },
        [projectDescriptionFields.PROJECT_DESCRIPTION]: {
            ...baseMultiLanguageEditorInput(appSettings.languages),
            name: projectDescriptionFields.PROJECT_DESCRIPTION,
            label: t('form.project.description.webField.label'),
            languageStyle: 'tabs',
            colProps: {
                sm: 12,
                xl: 6,
            },
        },
        [projectDescriptionFields.PROJECT_OFFER_DESCRIPTION]: {
            ...baseMultiLanguageEditorInput(appSettings.languages),
            name: projectDescriptionFields.PROJECT_OFFER_DESCRIPTION,
            label: t('form.project.description.offerField.label'),
            languageStyle: 'tabs',
            colProps: {
                sm: 12,
                xl: 6,
            },
        },
    }
}

export const projectDetailSearchFieldTypes = () => {
    const { t } = i18next

    return {
        [projectDetailSearchFields.INTERNAL_APP_ID]: {
            ...baseTextInput,
            name: projectDetailSearchFields.INTERNAL_APP_ID,
            label: t('app.common.projectId'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.PROJECT_TITLE]: {
            ...baseTextInput,
            name: projectDetailSearchFields.PROJECT_TITLE,
            label: t('app.common.project.label'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.CITY]: {
            ...baseSelectInput,
            name: projectDetailSearchFields.CITY,
            label: t('app.common.cityMunicipality'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.PROJECT_TYPE]: {
            ...baseSelectInput,
            name: projectDetailSearchFields.PROJECT_TYPE,
            label: t('app.common.project.type'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.PROJECT_STATUS]: {
            ...baseMultiSelectInput,
            name: projectDetailSearchFields.PROJECT_STATUS,
            label: t('app.common.status'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.AGENT]: {
            ...baseSelectInput,
            name: projectDetailSearchFields.AGENT,
            label: t('app.common.agent'),
            colProps: {
                sm: 12,
            },
        },
        [projectDetailSearchFields.PROJECT_RECEIPT_DATE_FROM]: {
            ...baseDateInput,
            colProps: { sm: 6, className: 'pe-0' },
            name: projectDetailSearchFields.PROJECT_RECEIPT_DATE_FROM,
            placeholder: t('app.common.from'),
        },
        [projectDetailSearchFields.PROJECT_RECEIPT_DATE_TO]: {
            ...baseDateInput,
            colProps: { sm: 6 },
            name: projectDetailSearchFields.PROJECT_RECEIPT_DATE_TO,
            placeholder: t('app.common.to'),
        },
    }
}
